import { Validators, FormBuilder } from '@angular/forms';
import { GeneralFormData } from '../general-form-data.model';
import { formatTimeForDisplay, parseLocalDate } from '../../../utils/date-time-formatter';
import { FormStep } from '../form-steps.interface';






export class NearMissReport {

    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;
    
    // ===============================================
    // STEP 1: Incident Type & Employee Information
    // ===============================================
    
    
    // Employee Information
    employeeFirstName: string = '';
    employeeLastName: string = '';
    dateOfBirth: Date | null = null;
    dateOfHire: Date | null = null;
    craftAndPosition: string = '';
    
    // ===============================================
    // STEP 2: Job Information & Incident Details
    // ===============================================
    
    // Job Information
    jobName: string = '';
    shiftWorked: string = '';
    supervisorFirstName: string = '';
    supervisorLastName: string = '';
    hoursWorkedPerDay!: number | null;
    
    // Near-Miss Information (timing, location, circumstances)
        // Type of Incident (checkboxes - select all that apply)
        injuryNearMiss: boolean = false;
        propertyDamage: boolean = false;
        otherNearMiss: boolean = false;
        specifyOther: string = '';
    dateOfIncident: Date | null = null;
    timeOfIncident: string = '';
    dateReported: Date | null = null;
    timeReported: string = '';
    incidentReportedToFirstName: string = '';
    incidentReportedToLastName: string = '';
    weather: string = '';
    incidentLocation: string = '';
    incidentDescription: string = '';
    equipmentTool1: string = '';
    equipmentTool2: string = '';
    anyDefects: string = '';
    coveredInPreTask: string = '';
    
    // ===============================================
    // STEP 3: Incident Analysis
    // ===============================================
    
    // More Near Miss Information (witnesses & PPE)
    witness1: string = '';
    witness2: string = '';
    wasPpeRequired: string = '';
    wasPpeAvailable: string = '';
    
    // Incident & Analysis
    othersInvolved: string = '';
    othersExplanation: string = '';
    preventionSuggestion: string = '';
    
    // ===============================================
    // STEP 4: Injury/Damage Assessment & Completion
    // ===============================================
    
    // Injury/Damage Assessment
    injured: string = '';
    couldHaveBeenInjured: string = '';
    bloodExposure: string = '';
    seekMedicalTreatment: string = '';
    previousExposure: string = '';
    aggravationOfPreviousInjury: string = '';
    describePreviousInjury: string = '';
    worstCaseScenario: string = '';
    hasPropertyDamage: string = '';
    damageDescription: string = '';
    uploads: Array<string> = [];
    signature: Array<string> = [];
    signatureDate: Date | null = null;

    constructor(data?: Partial<NearMissReport>) {
        if (data) {
            Object.assign(this, {
                ...data,
                dateOfBirth: data.dateOfBirth ? parseLocalDate(data.dateOfBirth as any) : null,
                dateOfHire: data.dateOfHire ? parseLocalDate(data.dateOfHire as any) : null,
                dateOfIncident: data.dateOfIncident ? parseLocalDate(data.dateOfIncident as any) : null,
                dateReported: data.dateReported ? parseLocalDate(data.dateReported as any) : null,
                signatureDate: data.signatureDate ? parseLocalDate(data.signatureDate as any) : null,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,

                //convert strings to numbers
                hoursWorkedPerDay: data.hoursWorkedPerDay ? Number(data.hoursWorkedPerDay) : 0,
                //convert time
                timeOfIncident: data.timeOfIncident ? formatTimeForDisplay(data.timeOfIncident) : '',
                timeReported: data.timeReported ? formatTimeForDisplay(data.timeReported) : '',
            });
        }
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'employee-job-details',
                shortTitle: 'Employee & Job',
                title: 'Employee & Job Details',
                icon: 'person-outline',
                description: 'Employee information and job details',
                sections: [
                    {
                        fields: ['employeeFirstName', 'employeeLastName', 'dateOfBirth', 'dateOfHire', 'craftAndPosition']
                    },
                    {
                        title: 'Job Information',
                        fields: ['jobName', 'shiftWorked', 'hoursWorkedPerDay', 'supervisorFirstName', 'supervisorLastName']
                    }
                ]
            },
            {
                id: 'incident-basics',
                shortTitle: 'Incident Basics',
                title: 'Incident Basics',
                icon: 'alert-circle-outline',
                description: 'Incident details, timing, and location',
                sections: [
                    
                    {checkboxGroups: [
                        {
                            fields: ['injuryNearMiss', 'propertyDamage', 'otherNearMiss'],
                            checkboxLabel: this.getFieldMappings()['injuryNearMiss']
                        }
                    ],
                    fields: ['specifyOther']                    },
                    {
                        fields: ['dateOfIncident', 'timeOfIncident', 'dateReported', 'timeReported', 'incidentReportedToFirstName', 'incidentReportedToLastName', 'weather', 'incidentLocation', 'incidentDescription']

                    },
                    {
                        title: 'Witnesses',
                        fields: ['witness1', 'witness2']
                    }
                ]
            },
            {
                id: 'equipment',
                shortTitle: 'Equipment',
                title: 'Equipment',
                icon: 'construct-outline',
                description: 'Equipment involved and safety measures',
                sections: [
                    {
                        fields: ['equipmentTool1', 'equipmentTool2', 'anyDefects', 'coveredInPreTask', 'wasPpeRequired', 'wasPpeAvailable']
                    }
                ]
            },
            {
                id: 'incident-assessment',
                shortTitle: 'Incident Assessment',
                title: 'Incident Assessment',
                icon: 'document-text-outline',
                description: 'Assessment of injuries, damage, and prevention',
                sections: [
                    {
                        title: 'Injury',
                        fields: ['othersInvolved', 'othersExplanation', 'injured', 'couldHaveBeenInjured', 'bloodExposure', 'seekMedicalTreatment', 'previousExposure', 'aggravationOfPreviousInjury', 'describePreviousInjury', 'worstCaseScenario', 'preventionSuggestion']
                    },
                    {
                        title: 'Damage',
                        fields: ['hasPropertyDamage', 'damageDescription']
                    },
                    {title: 'Uploads',
                        fields: ['uploads']
                    }
                ]
            },
            {
                id: 'sign-off',
                shortTitle: 'Sign Off',
                title: 'Sign Off',
                icon: 'pencil-outline',
                description: 'Sign off and completion',
                sections: [
                    {
                        fields: ['signatureDate', 'signature']
                    }
                ]
            }
        ];
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'near-miss-report',
            formTitle: 'Near Miss Report',
            formIcon: 'alert-circle-outline',
            cardImage: 'assets/form-card-tiles/near-miss-report.png',
            formAttachmentRequirements: {
                uploads: [{
                    identifier: 'uploads',
                    label: this.getFieldMappings()['uploads'],
                    required: 0
                }],
                signatures: [
                    {
                        identifier: 'signature',
                        label: this.getFieldMappings()['signature'],
                        required: true
                    }
                ]
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            submittedByName: 'Submitted By Name',
            submittedByEmail: 'Submitted By Email',
            submissionDate: 'Submission Date',
            // Basic Information
            typeOfNearMiss: 'Type of Near-Miss',
        injuryNearMiss: 'Injury Near-Miss',
        propertyDamage: 'Property Damage ($500 or More)',
        otherNearMiss: 'Other Near-Miss',
        specifyOther: 'Specify Other',
        employeeFirstName: 'Employee First Name',
        employeeLastName: 'Employee Last Name',
        dateOfBirth: 'Date of Birth',
        dateOfHire: 'Date of Hire',
        craftAndPosition: 'Craft and Position',
        jobName: 'Job Name',
        shiftWorked: 'Shift Worked',
        supervisorFirstName: 'Supervisor First Name',
        supervisorLastName: 'Supervisor Last Name',
        hoursWorkedPerDay: 'Hours Worked per Day',
        
        // Incident Details
        dateOfIncident: 'Date of Incident',
        timeOfIncident: 'Time of Incident',
        dateReported: 'Date Reported',
        timeReported: 'Time Reported',
        incidentReportedToFirstName: 'Incident Reported To (First Name)',
        incidentReportedToLastName: 'Incident Reported To (Last Name)',
        weather: 'Weather Conditions',
        incidentLocation: 'Exact Location of Incident',
        equipmentTool1: 'Equipment/Tools Involved #1',
        equipmentTool2: 'Equipment/Tools Involved #2',
        anyDefects: 'Any Defects',
        coveredInPreTask: 'Was the Task/Job Covered on the Pre-Task?',
        witness1: 'Witness Names/Contact Information #1',
        witness2: 'Witness Names/Contact Information #2',
        wasPpeRequired: 'Was PPE Required?',
        wasPpeAvailable: 'Was PPE Available?',
        
        // Analysis & Assessment
        incidentDescription: 'Description of Incident',
        othersInvolved: 'Were Others Involved?',
        othersExplanation: 'Explain Others Involved',
        preventionSuggestion: 'Prevention Suggestion',
        injured: 'Was Anyone Injured?',
        couldHaveBeenInjured: 'Could Anyone Have Been Injured?',
        bloodExposure: 'Was There Blood Exposure?',
        seekMedicalTreatment: 'Did Anyone Seek Medical Treatment?',
        previousExposure: 'Was There Previous Exposure?',
        aggravationOfPreviousInjury: 'Was There Aggravation of Previous Injury?',
        describePreviousInjury: 'Describe Previous Injury',
        worstCaseScenario: 'Worst Case Scenario',
        hasPropertyDamage: 'Was There Property Damage?',
        damageDescription: 'Damage Description',
        
        // Files and Signature
        uploads: 'Uploads',
        signature: 'Signature',
        signatureDate: 'Signature Date'
    };
    }
}